import React, { useEffect, useState } from 'react';
import { Input, Typography, Card, Spin, Avatar, Divider, message, Tooltip } from 'antd'; 
import { UserOutlined, LockOutlined, PhoneOutlined, MailOutlined, IdcardOutlined } from '@ant-design/icons';
import axios from '../../services/axios';

const { Title, Text } = Typography;

const ProfileManagement = () => {
  const [userData, setUserData] = useState(null); 
  const [loading, setLoading] = useState(true);
  const user = localStorage.getItem('user');

  const fetchUserData = async () => {
    try {
      const response = await axios.post('get-profile-data', { user });
      setUserData(response.data);
      setLoading(false); 
    } catch (err) {
      handleError(err);
      setLoading(false);
    }
  };

  const handleError = (err) => {
    if (err.response?.data?.msg) {
      message.warning(err.response.data.msg);
    } else if (err.response?.data?.userName?.[0]) {
      message.warning(err.response.data.userName[0]);
    } else if (err.response?.status === 401) {
      message.error('User not authorized');
    } else {
      message.error('Something went wrong');
    }
  };

  useEffect(() => {
    fetchUserData(); 
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spin size="large" tip="Loading profile information..." />
      </div>
    );
  }

  const getIconForField = (field) => {
    switch(field) {
      case 'email': return <MailOutlined className="text-blue-500" />;
      case 'phoneNumber': return <PhoneOutlined className="text-green-500" />;
      case 'username': return <IdcardOutlined className="text-purple-500" />;
      case 'role': return <LockOutlined className="text-red-500" />;
      default: return <UserOutlined className="text-gray-500" />;
    }
  };

  const renderProfileField = (label, field, placeholder) => (
    <div className="mb-4">
      <div className="flex items-center mb-1">
        {getIconForField(field)}
        <Text strong className="text-gray-700 ml-2">{label}:</Text>
      </div>
      <Input
        value={userData[field]}
        readOnly
        placeholder={placeholder}
        className="rounded-lg shadow-sm border-gray-300 hover:border-blue-400 focus:border-blue-500 transition-all"
        prefix={field === 'role' || field === 'email' ? <Tooltip title="This field cannot be edited">🔒</Tooltip> : null}
      />
    </div>
  );

  return (
    <div className="flex justify-center items-center min-h-screen px-4 py-8">
      <Card
        className="w-full max-w-3xl shadow-xl rounded-xl overflow-hidden"
        bodyStyle={{ padding: '24px' }}
        bordered={false}
      >
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Avatar 
              size={100} 
              icon={<UserOutlined />} 
              className="bg-blue-500 shadow-lg"
            />
          </div>
          <Title level={2} className="mb-0">{userData?.firstName} {userData?.lastName}</Title>
        </div>

        <Divider orientation="center">Profile Information</Divider>

        {userData ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {renderProfileField("First Name", "firstName", "Your first name")}
            {renderProfileField("Last Name", "lastName", "Your last name")}
            {renderProfileField("Email", "email", "Your email address")}
            {renderProfileField("Username", "username", "Your username")}
            {renderProfileField("Phone Number", "phoneNumber", "Your phone number")}
            {renderProfileField("Role", "role", "Your system role")}
          </div>
        ) : (
          <div className="p-8 text-center bg-red-50 rounded-lg">
            <Text type="danger" className="text-lg">No user data available. Please try refreshing the page.</Text>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ProfileManagement;
